import React from 'react';
import { Link } from 'react-router-dom';
import '../../styles/User.css';
import '../../styles/UserProfile.css';
import '../../styles/Posts.css';
import usePublic from '../../hooks/usePublic';
import usePosts from '../../hooks/usePosts';
import Posts from './Posts';
import Create from '../Create';
import { useAuth } from '../../contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFacebook,
  faTwitter,
  faInstagram,
} from '@fortawesome/free-brands-svg-icons';
import { socialHref } from '../../utils/socialUrls';

function ProfileSocialBlock({ label, href, icon, className = '' }) {
  const safe = socialHref(href);
  const iconEl = <FontAwesomeIcon icon={icon} size="2x" />;
  return (
    <div className={`profile-social-card ${className}`.trim()}>
      <div className="text-center">
        <p className="profile-social-label">{label}</p>
        {safe ? (
          <a href={safe} target="_blank" rel="noopener noreferrer">
            {iconEl}
          </a>
        ) : (
          <span className="profile-social-icon-muted" aria-hidden="true">
            {iconEl}
          </span>
        )}
      </div>
    </div>
  );
}

export default function User({ match }) {
  const username = match.params.username;
  const { docs, loading, error, notFound } = usePublic(username);
  const { posts, error: postsError } = usePosts({
    username,
    profileUid: docs?.uid,
  });
  const { currentUser } = useAuth();

  if (loading) {
    return (
      <div className="user-profile-state">
        <p>Loading profile...</p>
      </div>
    );
  }

  if (error || postsError) {
    return (
      <div className="user-profile-state">
        <p>Could not load profile. Check your internet connection and try again.</p>
        <p className="user-profile-state-detail">
          {error || postsError}
        </p>
        <button
          type="button"
          className="user-profile-retry-btn"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  if (notFound || !docs) {
    return (
      <div className="user-profile-state">
        <p>User @{username} not found.</p>
        <Link to="/search" className="user-profile-retry-btn">
          Search users
        </Link>
      </div>
    );
  }

  return (
    <div className="user-profile-wrap user-profile-page">
      <div
        className="profile-cover"
        style={{
          backgroundImage: docs.header
            ? `url(${docs.header})`
            : 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
        }}
      />

      <div className="profile-header row justify-content-center">
        <div className="profile-avatar-col no-padding col-sm-4 col-3">
          <img
            className="profile-avatar center rounded-circle p-0"
            src={docs.img}
            alt="Profile"
          />
        </div>
        <div className="col-sm-2 col-3 d-flex align-items-center justify-content-center">
          <div className="w-100">
            <Link className="profile-nav-link" to="#">
              Followers
            </Link>
          </div>
        </div>
        <div className="col-sm-2 col-3 d-flex align-items-center justify-content-center">
          <div className="w-100">
            <Link className="profile-nav-link" to="#">
              Following
            </Link>
          </div>
        </div>
        <div className="col-sm-2 col-3 d-flex align-items-center justify-content-center">
          <div className="w-100">
            <Link className="profile-nav-link" to="#">
              Images
            </Link>
          </div>
        </div>
        <div className="col-sm-2 col-3 d-flex align-items-center justify-content-center">
          <div className="w-100">
            <Link className="profile-nav-link" to="#">
              Network
            </Link>
          </div>
        </div>
      </div>

      <aside className="profile-sidebar sidebar d-none d-lg-block">
        <ProfileSocialBlock
          label="Twitter"
          href={docs.twitterUrl}
          icon={faTwitter}
        />
        <ProfileSocialBlock
          label="Instagram"
          href={docs.instagramUrl}
          icon={faInstagram}
          className="mt-3"
        />
        <ProfileSocialBlock
          label="Facebook"
          href={docs.facebookUrl}
          icon={faFacebook}
          className="mt-3"
        />
        <div className="text-center mt-3">
          <button type="button" className="profile-blog-btn">
            Blog (coming soon)
          </button>
        </div>
      </aside>

      <main className="profile-content content mt-5">
        <div className="profile-name-block name">
          <div className="dropdown">
            <h3 className="profile-display-name">{docs.name}</h3>
            <p
              className="profile-invite-btn dropdown-toggle"
              type="button"
              id="dropdownMenuButton"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            >
              Invite Friends
            </p>
            <div className="dropdown-menu" aria-labelledby="dropdownMenuButton" />
          </div>
        </div>

        <h3 className="profile-section-title">About the user</h3>
        <div className="profile-bio user-info mb-3">
          <p className="pl-2 pt-1 mb-0">
            {docs.bio ? docs.bio : 'Nothing entered...'}
          </p>
        </div>

        <h3 className="profile-section-title">Posts</h3>
        <div>
          {currentUser && docs.uid === currentUser.uid && <Create />}
          <Posts posts={posts} />
        </div>

        <div className="profile-mobile-social d-md-none">
          <h3 className="profile-section-title">Social</h3>
          <ProfileSocialBlock
            label="Twitter"
            href={docs.twitterUrl}
            icon={faTwitter}
          />
          <ProfileSocialBlock
            label="Instagram"
            href={docs.instagramUrl}
            icon={faInstagram}
            className="mt-2"
          />
          <ProfileSocialBlock
            label="Facebook"
            href={docs.facebookUrl}
            icon={faFacebook}
            className="mt-2"
          />
        </div>
      </main>
    </div>
  );
}
